"use client";

import { useEffect } from "react";

import { useStudio } from "@/contexts/studio-context";
import { TICK_EPSILON } from "@/lib/harmony/cursor";

/**
 * Le niveau déplace la tête de lecture du studio, d'une porte à la suivante.
 *
 * ## Le studio avance, et tout le reste suit parce qu'il avance
 *
 * La partition n'a pas d'horloge : elle lit `getEffectiveTick()` à chaque
 * image et le traduit en pixels. Pour qu'elle avance pendant qu'on traverse
 * une fenêtre, c'est donc **la tête de lecture du studio** qui doit avancer.
 * Le roll, la minimap et la partition la suivent alors ensemble, sans que rien
 * n'ait à se synchroniser avec rien.
 *
 * ## Un défilement, et non une lecture
 *
 * `animateScrollToTick` et non `play()`. C'est le chemin par lequel
 * l'application déplace déjà la tête de lecture toute seule — le parcours par
 * cœur s'en sert à chaque attaque d'un passage — et il a la propriété qui
 * décide ici : le transport reste **en pause**. Le faire jouer aurait déplacé
 * la tête aussi bien, mais aurait changé le comportement de cinq objets qui
 * n'ont rien à voir avec cet écran. Voir la note de `harmony-scene` sur le
 * régime, et `piano-controler`, `section-tips`, `view-navigator`,
 * `piano-roll-component` et le gouverneur de rendu de `studio-view`.
 *
 * La durée imposée est celle du segment, et c'est tout ce que ce module
 * ajoute au défilement du studio : sans elle, une porte à une noire de la
 * suivante serait franchie en quatre dixièmes puis l'écran attendrait, ce qui
 * est l'inverse d'un repère de rythme.
 *
 * ## Il se parcourt d'un bout à l'autre, donc il part de son début
 *
 * La durée du segment est celle du chemin qu'il décrit, de sa frappe à la
 * suivante. Partir d'ailleurs ferait parcourir une autre distance dans ce
 * temps-là, donc à une autre vitesse, et le repère de rythme mentirait. Or la
 * tête peut très bien être ailleurs : à l'ouverture d'un niveau le transport
 * est au début du morceau alors que la première fenêtre est plus loin, et le
 * joueur peut l'avoir déplacée à la main.
 *
 * On la pose donc, mais **sans arracher l'image** : le joueur frappe la porte
 * suivante à l'instant où elle s'ouvre, c'est-à-dire avant que le vol précédent
 * ait atterri — la porte compte depuis la frappe, le vol depuis le premier son.
 * `seekWithoutJump` fait absorber le déplacement par le décalage en cours, et
 * rien ne bouge à l'écran. Plus il y avait d'attaques dans une mesure, plus la
 * scène sautait.
 *
 * ## Ce que ça fait à l'arrivée
 *
 * Le défilement se conclut par un `seekToTick` sur la porte visée : le
 * transport y est vraiment, et non pas seulement l'image. C'est ce qui fait
 * que l'accord ancré, le rail et la minimap disent tous la même chose
 * que la partition à la fin de chaque segment, sans code de raccord.
 *
 * ## Pendant le vol, personne ne doit prendre la tête
 *
 * Tant qu'il vole, **le transport ne bouge pas** : c'est le décalage visuel du
 * navigateur qui avance, et le transport ne saute sur la porte visée qu'au
 * dernier moment. Son commit calcule alors l'arrivée en **ajoutant** ce
 * décalage à la position du transport, et non en visant un tick absolu. Un
 * `seekToTick` qui tomberait entre-temps déplacerait le point d'où il compte,
 * et l'arrivée dépasserait d'autant : la fenêtre suivante se trouvait franchie
 * sans avoir été jouée.
 *
 * Rien n'est à annoncer pour autant : un vol se voit, puisque `getEffectiveTick`
 * et la position du transport ne disent alors pas la même chose. Voir `settle`
 * dans `harmony-scene`, qui s'en sert et qui n'avait de toute façon plus rien à
 * faire pendant ce temps-là : le défilement mène exactement où il allait.
 *
 * ## Un segment qui continue vise une date, pas une durée
 *
 * Il est demandé un peu après sa porte : la frappe a pris de la grâce, et le
 * vol précédent a déjà déposé la tête au-delà. Lui donner sa durée entière
 * depuis là ferait aller la tête moins vite que le morceau, et elle
 * arriverait après lui. Le vol reçoit donc la date de fin du segment et met
 * ce qu'il en reste. Un segment qui repart part d'ici et de maintenant : il
 * met sa durée, comme avant.
 *
 * ## Il déborde, puis il revient
 *
 * Pendant la grâce, la tête continue au-delà de la porte à la vitesse du
 * segment qu'elle ouvre, exactement comme le son. Si la grâce s'écoule, elle
 * revient sur la porte d'un vol court — assez pour se voir comme un retour,
 * pas assez pour se lire comme une animation. C'est ce retour qui remet
 * d'accord le son, la tête et la porte : tous trois disent alors le même
 * endroit, celui d'où la frappe tardive repartira.
 */

/** Ce que met la tête pour revenir sur la porte après une grâce écoulée. */
const HALT_MS = 140;

export function useSegmentScroll({
  segment,
  ms: runningMs,
  grace,
  haltTick,
}: {
  /**
   * Le segment à parcourir, ou `null` quand il n'y a rien à traverser.
   *
   * La même valeur que celle qu'on fait entendre : le mouvement et le son
   * décrivent le même passage, et les brancher sur deux sources différentes
   * les ferait diverger au premier cas limite.
   */
  readonly segment: {
    readonly id: string;
    readonly startTick: number;
    readonly endTick: number;
    /**
     * Quand il finit, sur l'horloge de `performance.now()` — `null` sur un
     * segment qui repart, qui met alors `ms` depuis maintenant.
     */
    readonly endsAt: number | null;
  } | null;
  /** Ce que le segment dure, en millisecondes entendues. */
  readonly ms: number;
  /**
   * La porte est ouverte et le son continue : jusqu'où la tête déborde, et
   * jusqu'à quand sur l'horloge de `performance.now()`. `null` hors grâce.
   */
  readonly grace: { readonly tick: number; readonly until: number } | null;
  /** La grâce s'est écoulée : la porte où revenir. `null` sinon. */
  readonly haltTick: number | null;
}): void {
  const { studio } = useStudio();
  const id = segment?.id ?? null;
  const startTick = segment?.startTick ?? 0;
  const endTick = segment?.endTick ?? 0;
  const endsAt = segment?.endsAt ?? null;

  useEffect(() => {
    if (!studio || id === null) {
      return;
    }

    const ms = endsAt === null ? runningMs : endsAt - performance.now();

    if (ms <= 0) {
      return;
    }

    const transport = studio.getLocalTransport();

    if (Math.abs(transport.currentTick() - startTick) > TICK_EPSILON) {
      studio.seekWithoutJump(startTick);
    }

    void studio.animateScrollToTick(endTick, ms);
  }, [studio, id, startTick, endTick, endsAt, runningMs]);

  const graceTick = grace?.tick ?? null;
  const graceUntil = grace?.until ?? 0;

  useEffect(() => {
    if (!studio || graceTick === null) {
      return;
    }

    const ms = graceUntil - performance.now();

    if (ms <= 0) {
      return;
    }

    void studio.animateScrollToTick(graceTick, ms);
  }, [studio, graceTick, graceUntil]);

  useEffect(() => {
    if (!studio || haltTick === null) {
      return;
    }

    void studio.animateScrollToTick(haltTick, HALT_MS);
  }, [studio, haltTick]);
}
